import {
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import LucideIcon from '../Icons/LucideIcon';
import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { postMediaBigImages } from '@/src/apis/rest/mediaRest';
import { Media } from '@/src/models/MeidaDto';
import imageCompression from 'browser-image-compression';
import { useToast } from '@/src/hooks/useToast';
import { cn } from '@/src/utils';
import { formatter } from '@/src/utils/formatter';

export interface ImageUploadProps {
  onChange?: (media: Media, type: 'edit' | 'create') => void;
  topAccessary?: ({
    handleEdit,
    handleRemove,
  }: {
    handleEdit: () => void;
    handleRemove: () => void;
  }) => ReactNode;
  value?: string;
  containerClassName?: string;
  inputClassName?: string;
  accept?: string;
  inputProps?: InputHTMLAttributes<HTMLElement>;
  disabled?: boolean;
  width?: number;
  height?: number;
  hasText?: boolean;
  hasSizeText?: boolean;
  widthText?: number;
  heightText?: number;
  imageClassName?: string;
  maxImageSizeMb?: number;
}

function ImageUpload({
  containerClassName,
  topAccessary,
  value,
  onChange,
  inputClassName,
  accept,
  inputProps,
  disabled = false,
  width = 128,
  height = 128,
  hasText = true,
  hasSizeText = false,
  widthText = width,
  heightText = height,
  imageClassName,
  maxImageSizeMb,
}: ImageUploadProps) {
  const { toast } = useToast();

  const ref = useRef<HTMLInputElement | null>(null);
  const [image, setImage] = useState<string | null>(value ?? null);

  const uploadImageMutation = useMutation({ mutationFn: postMediaBigImages });

  const handleRemove = () => {
    setImage(null);
  };

  const [isLoading, setIsLoading] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleResizeFile = async (file: File) => {
    const compressedFile = await imageCompression(file, {
      maxSizeMB: maxImageSizeMb,
    });

    return new File([compressedFile], file.name, { type: file.type });
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);

    try {
      const formData = new FormData();
      const fileList = e.target.files;

      for (let i = 0; i < (fileList?.length || 0); i++) {
        // 이미지 최적화 코드 (에러 불편하다고하면 주석 해제)
        // if (maxImageSizeMb) {
        //   const resizingFile = await handleResizeFile(fileList?.[i] as File);

        //   formData.append('image', resizingFile);
        // } else {
        formData.append('image', fileList?.[i] as File);
        // }
      }

      formData.append('folder', 'commons/');
      formData.append('service', 'BOS_WEB');

      const response = await uploadImageMutation.mutateAsync(formData);

      await onChange?.(response.rows[0], image ? 'edit' : 'create');
      if (value === undefined) {
        setImage(response.rows[0].bufferFullUrl);
      } else {
        onChange || setImage(response.rows[0].bufferFullUrl);
      }

      setIsLoading(false);
    } catch (e) {
      setImage(null);

      setIsLoading(false);
    }
  };

  useEffect(() => {
    setImage(value || null);
  }, [value]);

  const currentImageImageStyle = !image
    ? 'relative rounded-lg border border-dashed border-primary300 bg-primary100'
    : 'relative rounded-lg border';

  const inputCursorStype = disabled ? '' : 'cursor-pointer';

  return (
    <div
      style={{
        width,
        height,
      }}
      tabIndex={0}
      className={twMerge(currentImageImageStyle, containerClassName)}
    >
      {!image && !isLoading && (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1">
          <LucideIcon size={24} name="ImagePlus" color="primary" />

          <p className="break-keep text-center text-b13m text-primary">
            {hasText && '파일을 등록해주세요'}
            <br />
            {hasSizeText &&
              `(${widthText.toLocaleString()}*${heightText.toLocaleString()})`}
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="relative flex h-full w-full flex-col items-center justify-center gap-2 rounded-lg border-dashed border-primary300 bg-primary100">
          <LucideIcon
            name="Loader2"
            size={20}
            color="primary"
            className="animate-spin"
          />
          {/* 이미지 최적화 코드 (에러 불편하다고하면 주석 해제) */}
          {/* {maxImageSizeMb && (
            <p className="text-b14r text-primary">이미지 최적화중..</p>
          )} */}
        </div>
      ) : (
        image && (
          <div className="relative flex h-full w-full">
            <div className="absolute right-[2px] top-[2px] z-30">
              {topAccessary?.({
                handleEdit: () => {
                  ref.current?.click();
                },
                handleRemove,
              })}
            </div>
            <img
              className={cn(
                'h-full w-full rounded-lg object-contain',
                imageClassName,
              )}
              src={image}
              alt="preview"
            />
          </div>
        )
      )}

      <input
        type="file"
        ref={ref}
        value=""
        className={twMerge(
          'absolute left-[50%] top-[50%] h-full w-full translate-x-[-50%]  translate-y-[-50%] transform opacity-0',
          inputCursorStype,
          inputClassName,
        )}
        onChange={async e => {
          if (maxImageSizeMb) {
            // 용량 초과시 막기
            const maxSize = maxImageSizeMb * 1024 * 1024; //* 5MB 사이즈 제한
            const fileSize = e.target.files?.[0].size || 0; //업로드한 파일용량

            if (fileSize > maxSize) {
              toast({
                variant: 'error',
                title: '실패',
                description: `파일첨부 사이즈는 ${formatter.NUM_COMMA(maxImageSizeMb)}MB 이내로 가능합니다.`,
              });
              e.target.value = ''; //업로드한 파일 제거
              return;
            }
          }

          handleUpload(e);
        }}
        disabled={disabled || isLoading}
        accept={accept ?? 'image/gif, image/jpeg, image/png, image/jpg'}
        {...inputProps}
      />
    </div>
  );
}

export default ImageUpload;
